/** AuthContext — central auth state.
 *  - On mount, GET /auth/me to determine session
 *  - login / register / logout helpers; refresh handled by interceptor
 *  - user === null while loading; user === false when not authenticated
 */
import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = anon, object = authed
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch (e) {
      setUser(false);
      return null;
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    setError(null);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      return data;
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      setError(msg);
      throw new Error(msg);
    }
  }, []);

  const register = useCallback(async (payload) => {
    setError(null);
    try {
      const { data } = await api.post("/auth/register", payload);
      setUser(data);
      return data;
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      setError(msg);
      throw new Error(msg);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (_) {
      // ignore — still clear locally
    }
    setUser(false);
  }, []);

  const logoutAll = useCallback(async () => {
    await api.post("/auth/logout-all");
    setUser(false);
  }, []);

  const value = { user, error, refresh, login, register, logout, logoutAll, setError };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}

/** Lightweight permission checker.
 *  Currently checks role hierarchy + `permissions` array on the user.
 *  Server is the source of truth; this is for UI visibility hints only.
 */
export function userCan(user, perm) {
  if (!user) return false;
  if (user.role === "super_admin") return true;
  if (Array.isArray(user.permissions) && user.permissions.includes(perm)) return true;
  return false;
}

export function userIsAdmin(user) {
  if (!user) return false;
  return ["super_admin", "admin"].includes(user.role);
}

export function userIsStaff(user) {
  if (!user) return false;
  return user.role !== "student" && user.role !== "guest";
}
